import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatApiError, registerAccount } from '../../api/auth';
import '../Login/Login.css';

function splitFullName(full) {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return { first_name: '', last_name: '' };
  return {
    first_name: parts[0],
    last_name: parts.slice(1).join(' '),
  };
}

const Register = () => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (password !== confirmPassword) {
      setFormError(t('register.passwordMismatch'));
      return;
    }
    const emailTrim = email.trim();
    const usernameTrim = username.trim();
    const { first_name, last_name } = splitFullName(name);
    if (!first_name) {
      setFormError(t('register.nameRequired'));
      return;
    }

    if (!usernameTrim) {
      setFormError(t('register.usernameRequired'));
      return;
    }

    setIsSubmitting(true);
    try {
      await registerAccount({
        username: usernameTrim,
        email: emailTrim,
        password,
        password_confirm: confirmPassword,
        first_name,
        last_name,
        phone: phone.trim() || '',
      });
      navigate('/');
    } catch (err) {
      const msg = formatApiError(err.response?.data) || err.message || t('register.failed');
      setFormError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">AuraGlow</div>
        <h1 className="login-title">{t('register.title')}</h1>
        <p className="login-subtitle">{t('register.subtitle')}</p>

        <form className="login-form" onSubmit={handleSubmit}>
          <label className="login-label">
            <span className="login-label-text">
              {t('register.fullName')}<span className="required">*</span>
            </span>
            <input
              type="text"
              className="login-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('register.fullNamePlaceholder')}
              required
              autoComplete="name"
            />
          </label>

          <label className="login-label">
            <span className="login-label-text">
              {t('register.username')}<span className="required">*</span>
            </span>
            <input
              type="text"
              className="login-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t('register.usernamePlaceholder')}
              required
              autoComplete="username"
            />
          </label>

          <label className="login-label">
            <span className="login-label-text">
              {t('register.email')}<span className="required">*</span>
            </span>
            <input
              type="email"
              className="login-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('register.emailPlaceholder')}
              required
              autoComplete="email"
            />
          </label>

          <label className="login-label">
            <span className="login-label-text">{t('register.phone')}</span>
            <input
              type="tel"
              className="login-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={t('register.phonePlaceholder')}
              autoComplete="tel"
            />
          </label>

          <label className="login-label">
            <span className="login-label-text">
              {t('register.password')}<span className="required">*</span>
            </span>
            <div className="login-password-wrap">
              <input
                type="password"
                className="login-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('register.passwordPlaceholder')}
                required
                minLength={6}
                autoComplete="new-password"
              />
              <button type="button" className="password-toggle" aria-label="Toggle password visibility">
                👁
              </button>
            </div>
          </label>

          <label className="login-label">
            <span className="login-label-text">
              {t('register.confirmPassword')}<span className="required">*</span>
            </span>
            <input
              type="password"
              className="login-input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t('register.confirmPlaceholder')}
              required
              autoComplete="new-password"
            />
          </label>

          {isSubmitting && (
            <div className="login-verifying">
              <span className="spinner" aria-hidden="true" />
              <span>{t('register.creating')}</span>
            </div>
          )}

          {formError ? (
            <p className="register-form-error" role="alert">
              {formError}
            </p>
          ) : null}

          <button type="submit" className="login-submit-btn" disabled={isSubmitting}>
            {t('register.create')}
          </button>

          <p className="login-footer-text">
            {t('register.hasAccount')}{' '}
            <button type="button" className="login-link-btn" onClick={() => navigate('/login')}>
              {t('register.logIn')}
            </button>
          </p>
        </form>
      </div>
    </div>
  );
};

export default Register;
